import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Row, Select } from 'antd';
import { getPayTokenContract, withdrawTokens } from '../../common/smart-contracts/helpers/PayToken/PayToken';
import { ethers, parseEther } from 'ethers';
import { ETHPrice } from '../../common/payment-types.const';
import { getETHPrice } from '../../utils/blockchain';
import { roundOff } from '../../utils/roundOff';
import { convertScientificToNormal } from '../../utils/scientificToNormal';
import { utils } from 'web3';

interface PaymentDetails {
  availableBalance: string | null;
  userWallet: string | null;
}

interface IComponentProps {
  closeModal: () => void;
  modalOpened: boolean;
  paymentDetails: PaymentDetails;
}

export function TokensWithdrawalModal({ closeModal, modalOpened, paymentDetails }: IComponentProps) {
  const { userWallet, availableBalance } = paymentDetails;
  const [fee, setFee] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [ethPrice, setETHPrice] = useState<ETHPrice>();
  const [convertationCurrency, setConvertationCurrency] = useState<'USD' | 'EUR'>('USD');
  const [convertedAmount, setConvertedAmount] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const canActivate = useMemo(
    () => availableBalance && amount && amount.length && amount <= availableBalance && parseFloat(amount) > 0,
    [availableBalance, amount],
  );

  const amountError = useMemo(
    () =>
      !amount.length && !convertedAmount.length
        ? false
        : !/^[0-9.]*$/g.test(amount) ||
          !(parseFloat(amount) > 0) ||
          !/^[0-9.]*$/g.test(convertedAmount) ||
          !(parseFloat(convertedAmount) > 0),
    [amount, convertedAmount],
  );

  useEffect(() => {
    if (!ethPrice) {
      (async () => {
        const ethPrice = await getETHPrice();
        if (ethPrice.status === 200) {
          setETHPrice(ethPrice.data);
        }
      })();
    }
  }, [ethPrice]);

  useEffect(() => {
    if (modalOpened && userWallet && amount) {
      (async () => {
        const contract = await getPayTokenContract(userWallet);
        if (contract && parseFloat(amount) > 0) {
          try {
            const gasEstimate = await contract.withdraw.estimateGas(
              utils.toWei(parseEther(amount.slice(0, 18)), 'wei').slice(0, 18),
            );
            setFee(ethers.formatEther(gasEstimate));
          } catch (e) {
            console.error(e);
          }
        }
      })();
    }
  }, [modalOpened, userWallet, amount]);

  async function handleWithdraw() {
    if (userWallet) {
      const contract = await getPayTokenContract(userWallet);

      if (contract) {
        const result = await withdrawTokens(contract, amount);
        const interval = setInterval(async () => {
          const provider = new ethers.BrowserProvider(window.ethereum);
          const receipt = await provider.getTransactionReceipt(result.hash);

          if (receipt) {
            if (receipt.status === 1) {
              clearInterval(interval);
              window.location.reload();
            } else {
              clearInterval(interval);
            }
          }
        }, 1000);

        closeModal();
      }
    }
  }

  return (
    <Modal
      title='Withdraw tokens to your wallet'
      className='tokens-withdrawal-modal'
      footer={
        <Row style={{ width: '100%', justifyContent: 'center' }}>
          <Button type='primary' disabled={amountError || !canActivate} onClick={handleWithdraw}>
            Withdraw
          </Button>
          <Button onClick={closeModal}>Cancel</Button>
        </Row>
      }
      open={modalOpened}
      onCancel={closeModal}
    >
      <p style={{ opacity: 0.55, fontSize: 16, textAlign: 'center' }}>
        Blockchain fees are charged for withdrawing
        <br /> tokens to a wallet
      </p>
      <div style={{ marginTop: '2em' }}>
        <p>
          <b>Available Balance:</b>
          <span style={{ marginLeft: 8 }}>
            {ethPrice && availableBalance
              ? roundOff(
                  convertScientificToNormal((parseFloat(availableBalance) * ethPrice[convertationCurrency]).toString()),
                )
              : 'N/A'}{' '}
            {convertationCurrency} ({availableBalance} ETH)
          </span>
        </p>
        <b>Amount to Withdraw:</b>
        <div className='grid-container' style={{ gap: 10, margin: '10px 0', alignItems: 'center' }}>
          <Input
            value={convertedAmount}
            style={{ padding: '4px 11px', ...(amountError && { borderColor: 'red' }) }}
            onChange={e => {
              let { value } = e.target;
              value = value.replaceAll(',', '.');
              setConvertedAmount(value);

              if (parseFloat(value) > 0) {
                const amount = parseFloat(value) / (ethPrice ? ethPrice[convertationCurrency] : 1);
                setAmount(amount.toString());
              } else {
                setAmount('');
              }
            }}
            placeholder='Transaction amount (Fiat currency)'
            className='grid-item'
          />
          <Select
            rootClassName={'select-root-manual'}
            style={{ height: 32 }}
            onDropdownVisibleChange={open => setDropdownOpen(open)}
            onChange={value => {
              if (amount && convertedAmount) {
                setAmount((parseFloat(convertedAmount) / (ethPrice ? ethPrice[value] : 1)).toString());
              } else if (amount) {
                setConvertedAmount((parseFloat(amount) * (ethPrice ? ethPrice[convertationCurrency] : 1)).toString());
              }

              setConvertationCurrency(value);
            }}
            options={[
              { value: 'USD', label: dropdownOpen ? 'USD - United States Dollar' : 'USD' },
              { value: 'EUR', label: dropdownOpen ? 'EUR - Euro' : 'EUR' },
            ]}
            placeholder='Transaction currency'
            value={convertationCurrency}
            className='grid-item'
          />
          <Input
            value={amount}
            style={{ padding: '4px 11px', ...(amountError && { borderColor: 'red' }) }}
            onChange={e => {
              let { value } = e.target;
              value = value.replaceAll(',', '.');
              setAmount(value);

              if (parseFloat(value) > 0) {
                const amount = parseFloat(value) * (ethPrice ? ethPrice[convertationCurrency] : 1);
                setConvertedAmount(amount.toString());
              } else {
                setConvertedAmount('');
              }
            }}
            placeholder='Store value amount'
            className='grid-item'
          />
          <span className='grid-item'>ETH</span>
          {amountError && <p style={{ color: 'red' }}>Amount must be a positive value</p>}
        </div>
        <p>
          <b>Wallet:</b>
          <span style={{ marginLeft: 8 }}>{userWallet}</span>
        </p>
        <p>
          <b>Fee:</b>
          {/* <span style={{ marginLeft: 8 }}>
            {ethPrice && fee
              ? roundOff(convertScientificToNormal((parseFloat(fee) * ethPrice[convertationCurrency]).toString()))
              : 'N/A'}{' '}
            {convertationCurrency} ({fee} ETH)
          </span> */}
          <span style={{ marginLeft: 8 }}>
            0.5 {convertationCurrency} ({fee} ETH)
          </span>
        </p>
      </div>
    </Modal>
  );
}
